/**
 * PressableSurface — React web implementation.
 */

import React, { useId } from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import type { PressableSurfaceProps } from './PressableSurface';
import { useDelegatedAnalyticsAction } from '../../analytics/AnalyticsActionContext';

export interface WebPressableSurfaceProps extends PressableSurfaceProps {
  onPress?: () => void;
  accessibilityHint?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  /** Roving-tabindex hook for composite widgets (tab lists, radio groups) that
   * manage one tab stop across their children (ADR-025 Rule 1). */
  tabIndex?: number;
  children?: React.ReactNode;
}

const PRESSABLE_SURFACE_FOCUS_STYLE_ID = 'galapagos-pressable-surface-focus-visible';
const PRESSABLE_SURFACE_FOCUS_SELECTOR = '[data-galapagos-pressable-surface="true"]';

export const PressableSurface = React.forwardRef<HTMLButtonElement, WebPressableSurfaceProps>(
  function PressableSurface(
    {
      role = 'button',
      disabled = false,
      selected,
      expanded,
      accessibilityLabel,
      accessibilityHint,
      testID,
      onPress,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      tabIndex,
      children,
      actionId = 'ui.surface.activate',
      elementKey,
    }: WebPressableSurfaceProps,
    ref,
  ) {
    ensureFocusVisibleStyle(PRESSABLE_SURFACE_FOCUS_STYLE_ID, [PRESSABLE_SURFACE_FOCUS_SELECTOR]);

    // Layout-neutral (ADR-020 Rule 2): strip the native button chrome so the
    // consumer's content styling shows through; add no surface/margin/position.
    // `margin` is absent deliberately — the rule forbids it on a root in any
    // direction, including a `0` reset, so external spacing stays the parent's.
    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      border: 'none',
      background: 'transparent',
      padding: 0,
      font: 'inherit',
      color: 'inherit',
      textAlign: 'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer',
    };

    // `selected` maps to the ARIA state that matches the role: a tab is
    // `aria-selected`, everything else (a toggle-ish button) is `aria-pressed`.
    const isTab = role === 'tab';
    const isRadio = role === 'radio';
    // Mirror the native activation contract: an inert surface (no onPress, or
    // disabled) performs no action, so it must not advertise one to the
    // delegated click listener either.
    const delegatedAction = useDelegatedAnalyticsAction(actionId);
    const activeActionId = onPress && !disabled ? delegatedAction : undefined;

    // The schema declares a hint and native forwards it, so web has to announce
    // it too or the same document reads differently on the two platforms. It goes
    // through `aria-describedby` pointing at a visually-hidden node rather than
    // `aria-description`, which is an ARIA 1.3 draft attribute with effectively no
    // screen-reader support — setting it would look like parity without being it.
    // `useId` rather than deriving from testID: two hint-bearing surfaces rendered
    // without a testID would otherwise emit the same DOM id, and
    // `aria-describedby` resolves to the first — so the second control would
    // announce the wrong hint.
    const generatedId = useId();
    const hintId = accessibilityHint
      ? `${testID ?? 'pressable-surface'}-hint-${generatedId}`
      : undefined;

    return (
      <button
        ref={ref}
        type="button"
        role={role === 'button' ? undefined : role}
        aria-label={ariaLabel ?? accessibilityLabel}
        aria-describedby={hintId}
        aria-disabled={disabled || undefined}
        aria-expanded={expanded}
        aria-selected={isTab ? selected : undefined}
        aria-checked={isRadio ? selected : undefined}
        aria-pressed={isTab || isRadio ? undefined : selected}
        data-galapagos-pressable-surface="true"
        data-analytics-action={activeActionId}
        // The delegated listener lifts the element key only alongside an active
        // action, so suppression (activeActionId undefined) silences both.
        data-analytics-element-key={activeActionId ? elementKey : undefined}
        data-testid={dataTestId ?? testID}
        tabIndex={tabIndex}
        style={style}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onPress?.();
        }}
      >
        {children}
        {hintId ? (
          // Clipped rather than `display: none` / `visibility: hidden`, both of
          // which remove the node from the accessibility tree and would leave
          // `aria-describedby` pointing at nothing.
          <span
            id={hintId}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {accessibilityHint}
          </span>
        ) : null}
      </button>
    );
  },
);
